/**
 * Maximum number of simultaneously open right-side entity drawers.
 * Enforced in the drawerOrder write path in state/global.state.tsx and
 * mirrored by the fixed slot count in components/legacy-ui/Drawer.tsx —
 * the renderer shows only the newest MAX_OPEN_DRAWERS entries.
 * Note: the NotificationsDrawer is rendered as an extra child of Drawers
 * outside drawerOrder and is NOT counted against this cap — the renderer
 * grants such children their own extra render slot (the `uncounted` prop on
 * Drawer), so an open NotificationsDrawer never evicts a counted drawer.
 * WARNING: if this cap is ever raised past 3, STACKED_DRAWER_WIDTHS in
 * components/legacy-ui/Drawer.tsx must be extended in the same change — it
 * defines per-slot widths for slots 1-3 (slot 0 is the parent, which sizes
 * itself; slot 3 exists because the uncounted extra child can push the
 * stack one past the cap). A slot index without an entry falls back to the
 * Drawer child default width, `calc(61% - 60px)` — identical to slot 1's
 * entry — so extra drawers would render exactly overlapping slot 1 with no
 * left-edge stagger.
 */
export const MAX_OPEN_DRAWERS = 3;

/**
 * Every drawer type that may appear in the `drawerOrder` URL param. The param
 * is user-editable (hand-crafted URLs, stale bookmarks carrying tokens from
 * removed drawer types), so every read goes through parseDrawerOrder in
 * state/global.state.tsx, which filters to this list — an unknown token must
 * neither render nor count toward MAX_OPEN_DRAWERS. When adding a drawer
 * type, extend this list; the DrawerType union and the parse sites derive
 * from it.
 */
export const DRAWER_TYPES = [
  'agent',
  'asset',
  'vulnerability',
  'seed',
  'integration',
  'threatIntel',
  'preseed',
  'technology',
  'capability',
  'customer',
  'parkedDomain',
] as const;

export type DrawerType = (typeof DRAWER_TYPES)[number];
